import React, { Suspense, lazy, useEffect } from 'react';
import { WorkspaceShell } from './modules/files/WorkspaceShell';
import { TenantProvider, useAuth } from './shell/TenantContext';
import { needsMfaEnrollment } from './lib/mfaPolicy';
import LoginPage from './pages/LoginPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import SSOCallbackPage from './pages/SSOCallbackPage';
import { useUiStore } from './store/uiStore';
import { useNavigationStore } from './store/navigationStore';
import { usePinnedAppsStore } from './store/pinnedAppsStore';
import { SearchModal } from './shell/SearchModal';
import { ReportIssueModal } from './shell/ReportIssueModal';
import { ConfirmDialogHost } from './design-system/components/ConfirmDialog';
import { ToastHost } from './design-system/components/Toast';
import { EmptyState } from './design-system/components/EmptyState';
import { Button } from './design-system/components/Button';
import { useUiFlagsStore, useUiV2, isUiV2 } from './shell/uiFlags';
import { DEFAULT_PAGE, DEFAULT_PAGE_V2 } from './shell/routes';
import { useOperatorLanding } from './shell/appFocus';
import { AUTHORIZE_PATH as TASK_MINING_AUTHORIZE_PATH, parkAuthorizeReturn, takeAuthorizeReturn } from './modules/task_mining/connect/authReturn';

const ConnectorGrid   = lazy(() => import('./modules/connectors/ConnectorGrid'));
const OntologyPage    = lazy(() => import('./modules/ontology/OntologyPage'));
const PipelineBuilder = lazy(() => import('./modules/pipeline/PipelineBuilder'));
const AppsPage        = lazy(() => import('./modules/apps/AppsPage'));
const DocumentsPage   = lazy(() => import('./modules/documents/DocumentsPage'));
const SolutionDesignPage = lazy(() => import('./modules/solutions/SolutionsPage'));
const WorkbenchPage   = lazy(() => import('./modules/workbench/WorkbenchPage'));
const PythonAppViewer = lazy(() => import('./modules/workbench/pyapps/PythonAppViewer'));
const ProjectsModule  = lazy(() => import('./modules/projects/ProjectsModule'));
const FinanceModule   = lazy(() => import('./modules/finance/FinanceModule'));
const LogicStudio     = lazy(() => import('./modules/logic/LogicStudio'));
const AgentStudio     = lazy(() => import('./modules/agents/AgentStudio'));
const HumanActions    = lazy(() => import('./modules/agents/HumanActionsPage'));
const ActionTypesPage = lazy(() => import('./modules/agents/ActionTypesPage'));
const UtilitiesPage   = lazy(() => import('./modules/utilities/UtilitiesPage'));
const SettingsPage    = lazy(() => import('./modules/settings/SettingsPage'));
const AccountPage     = lazy(() => import('./modules/account/AccountPage'));
const EvalsPage       = lazy(() => import('./modules/evals/EvalsPage'));
const ActivityPage    = lazy(() => import('./modules/activity/ActivityPage'));
const DataHubPage     = lazy(() => import('./modules/data/DataHubPage'));
const AdminHubPage    = lazy(() => import('./modules/admin/AdminHubPage'));
const ValuePage       = lazy(() => import('./modules/value/ValuePage'));
const SuperAdminPage  = lazy(() => import('./modules/superadmin/SuperAdminPage'));
const ProcessMiningV2 = lazy(() => import('./modules/process_v2/ProcessMiningV2'));
const OperationsModule = lazy(() => import('./modules/operations/OperationsModule'));
const SharePage       = lazy(() => import('./modules/share/SharePage'));
const LegalPage       = lazy(() => import('./pages/LegalPage'));
const ScenariosPage   = lazy(() => import('./modules/scenarios/ScenariosPage'));
const ExternalAppsPage = lazy(() => import('./modules/external_apps/ExternalAppsPage'));
const InsightsFeed     = lazy(() => import('./modules/insights/InsightsFeed'));
const InsightRunsPage  = lazy(() => import('./modules/insights/InsightRunsPage'));
const MfaEnrollmentPage = lazy(() => import('./pages/MfaEnrollmentPage'));
const PredictionsFeed  = lazy(() => import('./modules/predictions/PredictionsFeed'));
const DiscoveryPage    = lazy(() => import('./modules/discovery/DiscoveryPage'));
const TaskMiningPage   = lazy(() => import('./modules/task_mining/TaskMiningPage'));
const MirrorPage       = lazy(() => import('./modules/mirror/MirrorPage'));
const AcademyPage      = lazy(() => import('./modules/academy/AcademyPage'));
const AcademyCatalogPage = lazy(() => import('./modules/academy/AcademyCatalogPage'));
const IncidentsModule  = lazy(() => import('./modules/incidents/IncidentsModule'));
const BranchesPage     = lazy(() => import('./modules/release/BranchesPage'));
const ChangesPage      = lazy(() => import('./modules/release/ChangesPage'));
const VersionsPage     = lazy(() => import('./modules/release/VersionsPage'));
const SolutionsPage    = lazy(() => import('./modules/release/SolutionsPage'));
const FilesPage        = lazy(() => import('./modules/files/FilesPage'));
const ObjectPage       = lazy(() => import('./shell/ObjectPage'));
const HomePage         = lazy(() => import('./modules/home/HomePage'));
const TaskMiningConnectPage   = lazy(() => import('./modules/task_mining/connect/ConnectPage'));
const TaskMiningAuthorizePage = lazy(() => import('./modules/task_mining/connect/AuthorizePage'));
const TaskMiningDownloadPage  = lazy(() => import('./modules/task_mining/connect/DownloadPage'));

import { useTranslation } from 'react-i18next';
import { isPublicCatalogPath } from './modules/academy/academyRoute';

const LoadingSpinner: React.FC<{ message?: string }> = ({ message }) => {
  const { t } = useTranslation();
  const displayMsg = message || t('common.loading', 'Loading…');
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100%', gap: 12, color: '#475569',
      backgroundColor: '#F8FAFC',
    }}>
      <div style={{
        width: 20, height: 20, border: '2px solid #E2E8F0',
        borderTopColor: '#7C3AED', borderRadius: '50%',
        animation: 'spin 0.6s linear infinite',
      }} />
      <span style={{ fontSize: 12, letterSpacing: '0.04em' }}>{displayMsg}</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const ComingSoonPage: React.FC<{ title: string }> = ({ title }) => {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        height: 52, backgroundColor: '#fff', borderBottom: '1px solid #E2E8F0',
        display: 'flex', alignItems: 'center', padding: '0 24px', gap: 10,
      }}>
        <h1 style={{ fontSize: 15, fontWeight: 600, color: '#0D1117', margin: 0 }}>{title}</h1>
        <span style={{
          fontSize: 10, backgroundColor: '#F1F5F9', color: '#64748B',
          padding: '2px 8px', fontWeight: 600, letterSpacing: '0.06em',
        }}>
          {t('nav.soon', 'COMING SOON')}
        </span>
      </div>
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 8, color: '#94A3B8',
      }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{t('shell.underConstruction', 'This module is under construction')}</div>
        <div style={{ fontSize: 12 }}>{t('shell.checkBackSoon', 'Check back soon')}</div>
      </div>
    </div>
  );
};

// An unknown page id used to fall through to Connectors, which hid broken
// links (Cmd+K's Event Log and Process Mining both landed there).
const NotFoundPage: React.FC = () => {
  const { t } = useTranslation();
  const navigateTo = useNavigationStore(s => s.navigateTo);
  const uiV2 = useUiV2();
  return (
    <EmptyState
      title={t('shell.notFound', 'Page not found')}
      description={t('shell.notFoundHint', 'This link does not match any page.')}
      action={<Button onClick={() => navigateTo(uiV2 ? DEFAULT_PAGE_V2 : DEFAULT_PAGE)}>{t('shell.goHome', 'Go home')}</Button>}
    />
  );
};

const PageSuspense: React.FC<{ navKey?: string; fallbackName?: string; children: React.ReactNode }> = ({ navKey, fallbackName, children }) => {
  const { t } = useTranslation();
  const label = navKey ? t(navKey, fallbackName || '') : (fallbackName || '');
  const msg = label ? t('shell.loadingMessage', { module: label, defaultValue: `Loading ${label}...` }) : t('common.loading', 'Loading…');
  return <Suspense fallback={<LoadingSpinner message={msg} />}>{children}</Suspense>;
};

/** Ramas and Cambios: one Versiones area under ui_v2, the two pages as before without it. */
const VersionsGate: React.FC<{ page: 'branches' | 'changes' }> = ({ page }) => {
  const uiV2 = useUiV2();
  if (!uiV2) return page === 'branches' ? <BranchesPage /> : <ChangesPage />;
  return <VersionsPage initialTab={page === 'changes' ? 'requests' : 'home'} />;
};

export const renderPage = (page: string): React.ReactNode => {
  if (page.startsWith('object:')) {
    return <PageSuspense navKey="nav.ontology" fallbackName="Ontology"><ObjectPage page={page} /></PageSuspense>;
  }
  if (page.startsWith('app-')) {
    return (
      <PageSuspense navKey="nav.apps" fallbackName="Apps">
        <AppsPage />
      </PageSuspense>
    );
  }
  if (page === 'external-apps' || page.startsWith('external-app:')) {
    const installId = page.startsWith('external-app:') ? page.slice('external-app:'.length) : undefined;
    return (
      <PageSuspense navKey="nav.appStudio" fallbackName="App Studio">
        <ExternalAppsPage initialInstallId={installId} />
      </PageSuspense>
    );
  }
  switch (page) {
    case 'home':
      return <PageSuspense navKey="nav.home" fallbackName="Inicio"><HomePage /></PageSuspense>;
    case 'files':
      return <PageSuspense navKey="nav.files" fallbackName="Archivos"><FilesPage /></PageSuspense>;
    case 'connectors':
      return <PageSuspense navKey="nav.connectors" fallbackName="Connectors"><ConnectorGrid /></PageSuspense>;
    case 'ontology':
    case 'graph':
      return <PageSuspense navKey="nav.ontology" fallbackName="Ontology"><OntologyPage /></PageSuspense>;
    case 'pipelines':
      return <PageSuspense navKey="nav.pipelines" fallbackName="Pipelines"><PipelineBuilder /></PageSuspense>;
    case 'apps':
    case 'apps-app':
      return <PageSuspense navKey="nav.apps" fallbackName="Apps"><AppsPage /></PageSuspense>;
    case 'documents':
      return <PageSuspense navKey="nav.documents" fallbackName="Documentos"><DocumentsPage /></PageSuspense>;
    case 'solution-design':
      return <PageSuspense navKey="nav.solutionDesign" fallbackName="Diseño de soluciones"><SolutionDesignPage /></PageSuspense>;
    case 'workbench':
      return <PageSuspense navKey="nav.workbench" fallbackName="Workbench"><WorkbenchPage /></PageSuspense>;
    case 'projects':
      return <PageSuspense navKey="nav.projects" fallbackName="Projects"><ProjectsModule /></PageSuspense>;
    case 'finance':
      return <PageSuspense navKey="nav.finance" fallbackName="Finance"><FinanceModule /></PageSuspense>;
    case 'logic':
      return <PageSuspense navKey="nav.logicStudio" fallbackName="Logic Studio"><LogicStudio /></PageSuspense>;
    case 'agents':
      return <PageSuspense navKey="nav.agentStudio" fallbackName="Agent Studio"><AgentStudio /></PageSuspense>;
    case 'human-actions':
      return <PageSuspense navKey="nav.actions" fallbackName="Actions"><HumanActions /></PageSuspense>;
    case 'action-types':
      return <PageSuspense navKey="nav.ontology" fallbackName="Ontology"><ActionTypesPage /></PageSuspense>;
    case 'branches':
      return <PageSuspense navKey="nav.branches" fallbackName="Branches"><VersionsGate page="branches" /></PageSuspense>;
    case 'changes':
      return <PageSuspense navKey="nav.changes" fallbackName="Changes"><VersionsGate page="changes" /></PageSuspense>;
    case 'solutions':
      return <PageSuspense navKey="nav.solutions" fallbackName="Solutions"><SolutionsPage /></PageSuspense>;
    case 'utilities':
      return <PageSuspense navKey="nav.utilities" fallbackName="Utilities"><UtilitiesPage /></PageSuspense>;
    case 'account':
      return <PageSuspense navKey="nav.account" fallbackName="Account"><AccountPage /></PageSuspense>;
    case 'settings':
      return <PageSuspense navKey="nav.settings" fallbackName="Settings"><SettingsPage /></PageSuspense>;
    case 'evals':
      return <PageSuspense navKey="nav.evals" fallbackName="Evals"><EvalsPage /></PageSuspense>;
    case 'value':
      return <PageSuspense navKey="nav.value" fallbackName="Value Monitor"><ValuePage /></PageSuspense>;
    case 'activity':
      return <PageSuspense navKey="nav.activity" fallbackName="Activity"><ActivityPage /></PageSuspense>;
    case 'operations':
      return <PageSuspense navKey="nav.operations" fallbackName="Operations"><OperationsModule /></PageSuspense>;
    case 'data':
      return <PageSuspense navKey="nav.data" fallbackName="Data"><DataHubPage /></PageSuspense>;
    case 'data-analysis':
      return <PageSuspense navKey="nav.data" fallbackName="Data"><DataHubPage initialTab="analysis" /></PageSuspense>;
    case 'data-explorer':
      return <PageSuspense navKey="nav.data" fallbackName="Data"><DataHubPage initialTab="explorer" /></PageSuspense>;
    case 'data-grid':
      return <PageSuspense navKey="nav.data" fallbackName="Data"><DataHubPage initialTab="grid" /></PageSuspense>;
    case 'data-lineage':
      return <PageSuspense navKey="nav.data" fallbackName="Data"><DataHubPage initialTab="lineage" /></PageSuspense>;
    case 'data-datasets':
      return <PageSuspense navKey="nav.data" fallbackName="Data"><DataHubPage initialTab="datasets" /></PageSuspense>;
    case 'admin':
      return <PageSuspense navKey="nav.admin" fallbackName="Admin"><AdminHubPage /></PageSuspense>;
    case 'platform':
      return <PageSuspense navKey="nav.platform" fallbackName="Platform"><SuperAdminPage /></PageSuspense>;
    case 'incidents':
      return <PageSuspense navKey="nav.incidents" fallbackName="Incidents"><IncidentsModule /></PageSuspense>;
    case 'scenarios':
      return <PageSuspense navKey="nav.scenarios" fallbackName="Scenarios"><ScenariosPage /></PageSuspense>;
    case 'insights':
      return <PageSuspense navKey="nav.insights" fallbackName="Insights"><InsightsFeed /></PageSuspense>;
    case 'predictions':
      return <PageSuspense navKey="nav.predictions" fallbackName="Predictions"><PredictionsFeed /></PageSuspense>;
    case 'insights-runs':
      return <PageSuspense navKey="insights.runs" fallbackName="Run history"><InsightRunsPage /></PageSuspense>;
    case 'discovery':
      return <PageSuspense navKey="nav.discovery" fallbackName="Discovery"><DiscoveryPage /></PageSuspense>;
    case 'task-mining':
      return <PageSuspense navKey="nav.taskMining" fallbackName="Task Mining"><TaskMiningPage /></PageSuspense>;
    case 'mirror':
      return <PageSuspense navKey="mirror.title" fallbackName="Operational Mirror"><MirrorPage /></PageSuspense>;
    case 'academy':
      return <PageSuspense navKey="nav.academy" fallbackName="PAI Academy"><AcademyPage /></PageSuspense>;
    case 'process-mining':
      return <PageSuspense navKey="nav.processMining" fallbackName="Process Mining"><ProcessMiningV2 /></PageSuspense>;
    default:
      return <NotFoundPage />;
  }
};

// ── Theme + density sync ───────────────────────────────────────────────────

const ThemeSync: React.FC = () => {
  const { theme, density } = useUiStore();
  // `data-ui="v2"` lets the next steps of the redesign scope their CSS to
  // tenants on the new interface (A3: palette and density).
  const uiV2 = useUiFlagsStore(isUiV2);
  useEffect(() => {
    document.documentElement.setAttribute('data-ui', uiV2 ? 'v2' : 'v1');
  }, [uiV2]);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);
  useEffect(() => {
    document.documentElement.setAttribute('data-density', density);
  }, [density]);
  return null;
};

// ── Auth gate ──────────────────────────────────────────────────────────────

const AuthGate: React.FC = () => {
  const { isAuthenticated, currentUser, tenant } = useAuth();
  const setTenant = usePinnedAppsStore((s) => s.setTenant);
  const navigateTo = useNavigationStore((s) => s.navigateTo);
  // This gate picks its page from window.location, so re-read it when the URL
  // is replaced in place (SSOCallbackPage replaces it and fires popstate).
  const [, setLocationTick] = React.useState(0);
  useEffect(() => {
    const onPop = () => setLocationTick((n) => n + 1);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Once authenticated, set the pinned-apps store's tenant context, then
  // if this tenant has a home install configured, route to it. Only runs
  // when isAuthenticated flips — not on every render, and only when no
  // deep link in the URL is overriding the destination.
  useEffect(() => {
    if (!isAuthenticated || !tenant?.id) return;
    setTenant(tenant.id);

    const path = window.location.pathname;
    const hasDeepLink = path !== '/' && path !== '/index.html';
    if (hasDeepLink) return;

    // SharePage parks `/s/{token}` here before bouncing to `/` for sign-in.
    // After auth succeeds, return the user to the share link so the share
    // session can be minted seamlessly.
    try {
      const shareReturn = sessionStorage.getItem('nexus_share_return_to');
      if (shareReturn && shareReturn.startsWith('/s/')) {
        sessionStorage.removeItem('nexus_share_return_to');
        window.location.href = shareReturn;
        return;
      }
    } catch {
      /* ignore — sessionStorage may be blocked */
    }

    // Same for a task-mining device authorization interrupted by sign-in.
    const authorizeReturn = takeAuthorizeReturn();
    if (authorizeReturn) {
      window.history.replaceState(null, '', authorizeReturn);
      window.dispatchEvent(new Event('popstate'));
      return;
    }

    const home = usePinnedAppsStore.getState().home;
    if (home) navigateTo('external-app:' + home);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, tenant?.id]);

  // Interface flag (ui_v2) for the signed-in tenant. With it on, a visit to
  // the bare root lands on Home instead of Dashboards. Declared after the
  // effect above so a pinned home app, a share return or a deep link (which
  // all move off '/') win; replace, not push, so Back does not return to '/'.
  const tenantForFlags = tenant?.id;
  const loadUiFlags = useUiFlagsStore(s => s.load);
  const uiV2 = useUiV2();
  useEffect(() => {
    if (isAuthenticated && tenantForFlags) void loadUiFlags(tenantForFlags);
  }, [isAuthenticated, tenantForFlags, loadUiFlags]);
  useEffect(() => {
    if (!isAuthenticated || !uiV2) return;
    const path = window.location.pathname;
    if (path !== '/' && path !== '/index.html') return;
    const nav = useNavigationStore.getState();
    if (nav.currentPage === DEFAULT_PAGE && !nav.pendingAppId) nav.redirectTo(DEFAULT_PAGE_V2);
  }, [isAuthenticated, uiV2]);
  // An operator-only profile lands on its app list instead (C5, 3.34).
  useOperatorLanding(isAuthenticated, uiV2);

  // Handle SSO callback URL (/auth/callback?token=...)
  if (window.location.pathname === '/auth/callback') {
    return <SSOCallbackPage />;
  }

  const isTaskMiningAuthorize = window.location.pathname.replace(/\/$/, '') === TASK_MINING_AUTHORIZE_PATH;
  if (!isAuthenticated) {
    // Password sign-in keeps this URL; an SSO round trip does not, so park it.
    if (isTaskMiningAuthorize) parkAuthorizeReturn();
    return <LoginPage />;
  }
  if (currentUser?.mustChangePassword) return <ChangePasswordPage />;
  // Forced MFA enrollment: mandatory-role users must have MFA set up before
  // reaching the app (which roles is decided centrally in lib/mfaPolicy). Fires
  // only for an authenticated, mandatory-role user who has never enrolled. The
  // dev escape hatch VITE_DISABLE_MFA=true skips this entirely (defaults off).
  if (import.meta.env.VITE_DISABLE_MFA !== 'true' && needsMfaEnrollment(currentUser)) {
    return (
      <Suspense fallback={<LoadingSpinner message="Loading…" />}>
        <MfaEnrollmentPage />
      </Suspense>
    );
  }

  // Python apps published from a notebook (DS-5): /apps/python/<app_id>
  if (window.location.pathname.startsWith('/apps/python/')) {
    const appId = window.location.pathname.split('/apps/python/')[1]?.split(/[/?]/)[0] || '';
    return (
      <Suspense fallback={<LoadingSpinner message="Loading app..." />}>
        <div style={{ height: '100vh' }}><PythonAppViewer appId={appId} /></div>
      </Suspense>
    );
  }

  // Deep links to external apps: /apps/external/<install_id>
  if (window.location.pathname.startsWith('/apps/external/')) {
    const installId = window.location.pathname.split('/apps/external/')[1]?.split(/[/?]/)[0];
    return (
      <Suspense fallback={<LoadingSpinner message="Loading external app..." />}>
        <ExternalAppsPage initialInstallId={installId} />
      </Suspense>
    );
  }

  // Task Mining device authorization: the tray app's connect page sends
  // signed-in people here. Outside the shell and not gated by the module:
  // the service decides which projects this person may connect to.
  if (isTaskMiningAuthorize) {
    return (
      <Suspense fallback={<LoadingSpinner message="Loading…" />}>
        <TaskMiningAuthorizePage />
      </Suspense>
    );
  }

  // Hidden route: object-centric process mining v2. Not linked from nav —
  // accessible only by typing /pminingv2 in the URL bar.
  if (window.location.pathname === '/pminingv2') {
    return (
      <Suspense fallback={<LoadingSpinner message="Loading Process Mining v2..." />}>
        <ProcessMiningV2 />
      </Suspense>
    );
  }

  return (
    <>
      <SearchModal />
      <ReportIssueModal />
      <WorkspaceShell renderPage={renderPage} />
    </>
  );
};

// ── App ────────────────────────────────────────────────────────────────────

// Public policies live outside the auth gate: a data subject asking for
// erasure may have no account, and Meta's app review follows these URLs
// anonymously. Caddy's SPA fallback routes them here.
const PUBLIC_LEGAL_PATHS = ['/privacy', '/terms', '/legal', '/data-deletion'];

// Task Mining pages for people installing the desktop app, many of whom have
// no maic account: the page the app opens (approve with an invite code, or
// continue to the authenticated /task-mining/authorize) and the installers.
const TASK_MINING_PUBLIC_PAGES: Record<string, React.LazyExoticComponent<React.FC>> = {
  '/task-mining/connect': TaskMiningConnectPage,
  '/task-mining/download': TaskMiningDownloadPage,
};

function App() {
  return (
    <>
      <AppRoutes />
      {/* Mounted at the root, not in the shell: confirmDialog and toast are
          also called from the public share viewer and full-screen apps,
          which render outside it. */}
      <ConfirmDialogHost />
      <ToastHost />
    </>
  );
}

function AppRoutes() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  if (PUBLIC_LEGAL_PATHS.includes(path)) {
    return (
      <Suspense fallback={<LoadingSpinner message="Loading…" />}>
        <LegalPage />
      </Suspense>
    );
  }

  // PAI Academy's course catalog (GSL-3): public so a prospective learner can
  // browse every certification and its syllabus before enrolling.
  if (isPublicCatalogPath(path)) {
    return (
      <Suspense fallback={<LoadingSpinner message="Loading…" />}>
        <AcademyCatalogPage />
      </Suspense>
    );
  }

  const TaskMiningPublicPage = TASK_MINING_PUBLIC_PAGES[path];
  if (TaskMiningPublicPage) {
    return (
      <Suspense fallback={<LoadingSpinner message="Loading…" />}>
        <TaskMiningPublicPage />
      </Suspense>
    );
  }

  // Public share viewer — no tenant context, no auth gate. Token in the URL
  // is the credential. Path takes the form /s/<token>.
  if (window.location.pathname.startsWith('/s/')) {
    return (
      <Suspense fallback={<LoadingSpinner message="Loading…" />}>
        <SharePage />
      </Suspense>
    );
  }
  return (
    <TenantProvider>
      <ThemeSync />
      <AuthGate />
    </TenantProvider>
  );
}

export default App;
